// src/belediye/redux/slices/belediyeNotificationsSlice.js
//
//   Genel bildirim altyapısı (şimdilik close_approval provider)
//
// İSTENEN DÜZELTMELER (BU SÜRÜM):
// 1) close_request.cancelled bildirim olarak GÖSTERİLMEZ.
// 2) Okundu bilgisi login sonrası kaybolmasın diye readIds localStorage'da persist edilir. (  user bazlı)
// 3) Dropdown "limit=15" içinde okunmamışlar kaybolmasın diye unread-first selector eklendi.
// 4) Debug loglar genişletildi.
// 5) (Opsiyonel ama çok kritik) Polling'de dropdown "Yükleniyor…" zıplamasın diye silent fetch desteği.
//
// Backend (close_approval provider):
//   GET  /close-approval/close-requests/notifications/?unread=1&limit=100
//   POST /close-approval/close-requests/notifications/mark-read/  { ids: [..] }

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getNotifications as apiGetNotifications,
  markNotificationsRead as apiMarkNotificationsRead,
  getPreferences as apiGetPreferences,
  updatePreferences as apiUpdatePreferences,
} from "../../services/bildirimApi";

// -------------------------------------------------------------
// Debug helper
// -------------------------------------------------------------
const DEBUG = String(process.env.REACT_APP_DEBUG_CLOSE_APPROVAL || "").trim() === "1";

const log = (...args) => {
  if (DEBUG) console.log("[belediyeNotificationsSlice]", ...args);
};

const warn = (...args) => {
  if (DEBUG) console.warn("[belediyeNotificationsSlice]", ...args);
};

// -------------------------------------------------------------
// Safe helpers
// -------------------------------------------------------------
const safeStr = (v) => (v === null || v === undefined ? "" : String(v));
const safeJsonParse = (v) => {
  try {
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
};

const safeIds = (ids) =>
  (Array.isArray(ids) ? ids : [])
    .map((x) => Number(x))
    .filter((x) => Number.isFinite(x) && x > 0);

const uniqByIdDesc = (items) => {
  const seen = new Set();
  const out = [];
  for (const it of Array.isArray(items) ? items : []) {
    const id = Number(it?.id);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(it);
  }
  out.sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
  return out;
};

const normalizeError = (err, fallback = "İşlem başarısız.") => {
  const status = err?.response?.status;
  const data = err?.response?.data;

  if (data) {
    if (typeof data === "string") return data;
    if (typeof data?.detail === "string") return data.detail;

    if (typeof data === "object") {
      const firstKey = Object.keys(data)[0];
      const v = data[firstKey];
      if (Array.isArray(v) && v[0]) return String(v[0]);
      if (typeof v === "string") return v;
    }
  }

  if (status) return `${fallback} (HTTP ${status})`;
  return err?.message || fallback;
};

const readActiveUnitSlugSafe = () => {
  try {
    return String(localStorage.getItem("activeUnitSlug") || "").trim();
  } catch {
    return "";
  }
};

const readCurrentUserIdSafe = () => {
  try {
    const raw = localStorage.getItem("user");
    const u = safeJsonParse(raw);
    const id = Number(u?.id);
    return Number.isFinite(id) && id > 0 ? id : 0;
  } catch {
    return 0;
  }
};

// -------------------------------------------------------------
// LocalStorage persist (readIds) —   user bazlı
// -------------------------------------------------------------
const LS_KEY_READIDS_PREFIX = "belediyeNotifications.readIds.v1:";

const getReadIdsStorageKey = () => {
  const uid = readCurrentUserIdSafe();
  return `${LS_KEY_READIDS_PREFIX}${uid || "anon"}`;
};

const loadReadIdsFromStorage = () => {
  try {
    const key = getReadIdsStorageKey();
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};

    const out = {};
    for (const [k, v] of Object.entries(parsed)) {
      const id = Number(k);
      if (Number.isFinite(id) && id > 0 && v) out[id] = true;
    }
    return out;
  } catch {
    return {};
  }
};

// basit throttle (çok sık yazmayı engeller)
let _persistTimer = null;
const persistReadIdsToStorage = (readIds) => {
  try {
    if (_persistTimer) clearTimeout(_persistTimer);
    _persistTimer = setTimeout(() => {
      try {
        const key = getReadIdsStorageKey();
        localStorage.setItem(key, JSON.stringify(readIds || {}));
        if (DEBUG) {
          log("persistReadIdsToStorage:ok", {
            key,
            count: Object.keys(readIds || {}).length,
          });
        }
      } catch {
        //
      }
      _persistTimer = null;
    }, 250);
  } catch {
    //
  }
};

const clearReadIdsStorage = () => {
  try {
    const key = getReadIdsStorageKey();
    localStorage.removeItem(key);
  } catch {
    //
  }
};

// -------------------------------------------------------------
// Notification helpers
// -------------------------------------------------------------
const extractUnitSlugFromItem = (it) => {
  const direct = safeStr(it?.unit_slug).trim();
  if (direct) return direct;

  const payload = it?.payload || it?.inapp_payload?.payload;
  const fromPayload = safeStr(payload?.unit_slug).trim();
  if (fromPayload) return fromPayload;

  const fromAppUnit = safeStr(it?.application_unit?.slug).trim();
  if (fromAppUnit) return fromAppUnit;

  return "";
};

// FAZ-49: `close_request.cancelled` ARTIK GİZLENMİYOR (kullanıcı kararı
// 2026-07-26). Bildirim üretiliyor ama üç UI katmanında da gizlendiği için
// kullanıcı asla görmüyor → asla okundu yapamıyor → DB'de sonsuza dek
// okunmamış birikiyordu. "İşlemi yapana bildirim gitmez" kuralı zaten var,
// yani talep sahibi bunu ancak BAŞKASI iptal ettiğinde görür.
const shouldHideNotification = () => false;

const isReadByAny = (item, readIdsMap) => {
  const id = Number(item?.id);
  if (!id) return true;
  const backendIsRead = Boolean(item?.is_read);
  const localIsRead = Boolean(readIdsMap?.[id]);
  return backendIsRead || localIsRead;
};

const computeUnreadCount = (items, readIdsMap) => {
  let c = 0;
  for (const it of Array.isArray(items) ? items : []) {
    if (!isReadByAny(it, readIdsMap)) c += 1;
  }
  return c;
};

// FAZ-77 — sekme rozetleri için kaynak-başı sayaç bakımı.
// Otorite backend (`unread_by_source`); buradaki düşüşler yalnız İYİMSER
// güncelleme (tıklayınca rozet anında azalsın), sonraki fetch üzerine yazar.
const kaynakOf = (item) =>
  safeStr(item?.source_module || item?.inapp_payload?.source_module).trim();

const dusurKaynakSayaci = (state, item) => {
  const k = kaynakOf(item);
  if (!k) return;
  const mevcut = Number(state.unreadBySource?.[k] || 0);
  if (mevcut > 0) state.unreadBySource[k] = mevcut - 1;
};

const itemById = (state, id) =>
  (Array.isArray(state.items) ? state.items : []).find((x) => Number(x?.id) === Number(id));

const mergeItems = (prev, next) => {
  return uniqByIdDesc([
    ...(Array.isArray(next) ? next : []),
    ...(Array.isArray(prev) ? prev : []),
  ]);
};

// 3) Dropdown için: unread önce, sonra read (id desc)
const prioritizeUnreadThenRead = (items, readIdsMap) => {
  const list = Array.isArray(items) ? items : [];
  const unread = [];
  const read = [];

  for (const it of list) {
    if (!isReadByAny(it, readIdsMap)) unread.push(it);
    else read.push(it);
  }

  unread.sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
  read.sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
  return [...unread, ...read];
};

// -------------------------------------------------------------
// Thunks
// -------------------------------------------------------------
//   silent: polling'de loading banner zıplamasın diye
export const fetchNotifications = createAsyncThunk(
  "belediyeNotifications/fetch",
  async (
    { unreadOnly = true, limit = 40, mode = "merge", silent = false, sinceId, source } = {},
    { rejectWithValue }
  ) => {
    try {
      // FAZ-77: tavan 200 → 500 (api katmanı ve backend ile aynı sayı).
      const safeLimit = Math.max(1, Math.min(Number(limit) || 40, 500));
      const finalMode = mode === "replace" ? "replace" : "merge";

      log("fetch:start", {
        unreadOnly: Boolean(unreadOnly),
        limit: safeLimit,
        mode: finalMode,
        silent: Boolean(silent),
        sinceId: sinceId || null,
        activeUnitSlug: readActiveUnitSlugSafe(),
        userId: readCurrentUserIdSafe(),
      });

      // Merkezî bildirim çekirdektir → feature-flag kapısı YOK.
      const { items: fetched, unreadCount, unreadBySource } = await apiGetNotifications({
        unreadOnly: Boolean(unreadOnly),
        limit: safeLimit,
        sinceId: sinceId || undefined,
        source: source || undefined,
      });

      const raw = Array.isArray(fetched) ? fetched : [];

      const filteredOut = raw.filter((x) => !shouldHideNotification(x));
      const droppedCancelled = raw.length - filteredOut.length;

      const items = uniqByIdDesc(filteredOut);

      log("fetch:ok", {
        rawCount: raw.length,
        kept: items.length,
        droppedCancelled,
        unreadCount,
        silent: Boolean(silent),
      });

      return {
        items,
        unreadCount: Number(unreadCount || 0),
        unreadBySource: unreadBySource || {},
        unreadOnly: Boolean(unreadOnly),
        limit: safeLimit,
        mode: finalMode,
        fetchedAt: new Date().toISOString(),
        skipped: false,
        droppedCancelled,
        silent: Boolean(silent),
      };
    } catch (err) {
      warn("fetch:err", {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });
      return rejectWithValue(normalizeError(err, "Bildirimler alınamadı."));
    }
  }
);

export const markRead = createAsyncThunk(
  "belediyeNotifications/markRead",
  async ({ ids }, { rejectWithValue }) => {
    try {
      const cleaned = safeIds(ids);
      if (!cleaned.length) {
        log("markRead:skip (empty ids)");
        return { ids: [] };
      }

      log("markRead:start", { count: cleaned.length, ids: cleaned.slice(0, 10) });

      await apiMarkNotificationsRead(cleaned);

      log("markRead:ok", { count: cleaned.length });
      return { ids: cleaned };
    } catch (err) {
      warn("markRead:err", {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });
      return rejectWithValue(normalizeError(err, "Okundu işaretleme başarısız."));
    }
  }
);

// FAZ-49: "Tümünü okundu" — GERÇEKTEN tümü. Eskiden yalnız o an YÜKLÜ olan
// okunmamışların id'leri gönderiliyordu; 150'den fazla okunmamışta buton işini
// yapmıyordu. Backend `{all:true}` desteği ve api katmanı ZATEN vardı.
// FAZ-77: `source` verilirse kapsam AÇIK SEKMEyle sınırlı. Görevler
// sekmesindeyken "Tümünü okundu" duyuruları da okuyordu — gördüğün liste ile
// düğmenin yaptığı iş çelişiyordu.
export const markAllRead = createAsyncThunk(
  "belediyeNotifications/markAllRead",
  async ({ source } = {}, { rejectWithValue }) => {
    try {
      const data = await apiMarkNotificationsRead([], { all: true, source });
      log("markAllRead:ok", { updated: data?.updated, source: source || "hepsi" });
      return { updated: Number(data?.updated || 0), source: source || "" };
    } catch (err) {
      warn("markAllRead:err", { status: err?.response?.status, message: err?.message });
      return rejectWithValue(normalizeError(err, "Tümünü okundu işaretleme başarısız."));
    }
  }
);

// FAZ-33: kullanıcı bildirim tercihleri (ses/toast/mute)
export const fetchPreferences = createAsyncThunk(
  "belediyeNotifications/fetchPreferences",
  async (_arg, { rejectWithValue }) => {
    try {
      return await apiGetPreferences();
    } catch (err) {
      return rejectWithValue(normalizeError(err, "Bildirim ayarları alınamadı."));
    }
  }
);

export const savePreferences = createAsyncThunk(
  "belediyeNotifications/savePreferences",
  async (patch, { rejectWithValue }) => {
    try {
      return await apiUpdatePreferences(patch || {});
    } catch (err) {
      return rejectWithValue(normalizeError(err, "Bildirim ayarları kaydedilemedi."));
    }
  }
);

// -------------------------------------------------------------
// Slice
// -------------------------------------------------------------
const DEFAULT_PREFERENCES = {
  sound_enabled: true,
  toast_enabled: true,
  // FAZ-84: telefon bildirimi ana anahtarı. Varsayılan AÇIK ama tek başına
  // hiçbir şey göndermez — cihaz aboneliği olmadan push üretilmez.
  push_enabled: true,
  muted_categories: [],
  // FAZ-106 — tür aboneliği. `tur_tercihleri` YAZMA tarafı ({kod: bool});
  // `bildirim_turleri` sunucunun ürettiği EFEKTİF listedir (salt-okunur) ve
  // varsayılanı ROL belirlediği için ön yüzde üretilemez — bu yüzden burada
  // boş dizi: "sunucu daha konuşmadı" hâli, "hiç tür yok" değil.
  tur_tercihleri: {},
  bildirim_turleri: [],
  quiet_hours_start: null,
  quiet_hours_end: null,
};

const initialState = {
  items: [],

  // 2) login sonrası kaybolmasın diye persist readIds (  user bazlı key)
  readIds: loadReadIdsFromStorage(),

  loading: false,
  markingRead: false,
  error: null,

  lastFetchedAt: null,
  lastMode: null,

  unreadCountAll: 0,
  // FAZ-77: kaynak başına okunmamış — sekme rozetlerinin TEK kaynağı.
  unreadBySource: {},
  // FAZ-85: "sunucu sayıları söyledi mi?" — SAYININ KENDİSİNDEN ayrı bir bilgi.
  // Eskiden bu soru `Object.keys(unreadBySource).length > 0` ile soruluyordu;
  // yani "hepsi okundu" (0) ile "daha yüklenmedi" aynı cevabı veriyordu ve
  // sayaç tam sıfırlanması gereken anda bayat yerel hesaba düşüyordu.
  unreadLoaded: false,

  // FAZ-33: tercihler (backend'den; varsayılan ses/toast açık)
  preferences: DEFAULT_PREFERENCES,
  preferencesLoaded: false,
  preferencesSaving: false,
};

const belediyeNotificationsSlice = createSlice({
  name: "belediyeNotifications",
  initialState,
  reducers: {
    clearNotifications(state) {
      log("clearNotifications");
      state.items = [];
      state.readIds = {};
      state.loading = false;
      state.markingRead = false;
      state.error = null;
      state.lastFetchedAt = null;
      state.lastMode = null;
      state.unreadCountAll = 0;
      state.unreadBySource = {};
      state.unreadLoaded = false; // FAZ-85: oturum kapandı → sunucu bir daha konuşsun
      clearReadIdsStorage();
    },

    markOneAsReadLocal(state, action) {
      const id = Number(action.payload);
      if (!id) return;

      const zatenOkundu = Boolean(state.readIds[id]);
      state.readIds[id] = true;
      // İyimser düşüş: rozet anında azalır; bir sonraki fetch backend değerini
      // geri yazar (sayaç otoritesi backend — bkz. fetch.fulfilled).
      if (!zatenOkundu) {
        state.unreadCountAll = Math.max(0, Number(state.unreadCountAll || 0) - 1);
        dusurKaynakSayaci(state, itemById(state, id));
      }

      persistReadIdsToStorage(state.readIds);

      log("markOneAsReadLocal", {
        id,
        unreadCountAll: state.unreadCountAll,
        userId: readCurrentUserIdSafe(),
      });
    },

    //   login/logout sonrası (user değişince) doğru key'den hydrate etmek için
    hydrateReadIdsFromStorage(state) {
      state.readIds = loadReadIdsFromStorage();
      state.unreadCountAll = computeUnreadCount(state.items, state.readIds);
      log("hydrateReadIdsFromStorage", {
        persistedReadIds: Object.keys(state.readIds || {}).length,
        userId: readCurrentUserIdSafe(),
      });
    },
  },
  extraReducers: (builder) => {
    // fetch
    builder
      .addCase(fetchNotifications.pending, (state, action) => {
        const silent = Boolean(action?.meta?.arg?.silent);
        // 5) silent fetch => loading banner gösterme
        state.loading = !silent;
        state.error = null;
        log("fetch:pending", { silent, arg: action?.meta?.arg || {} });
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        const silent = Boolean(action?.payload?.silent);
        if (!silent) state.loading = false;

        const { items, mode, fetchedAt, skipped, droppedCancelled } = action.payload || {};
        const incoming = Array.isArray(items) ? items : [];

        // defans: cancelled item state'e girmesin
        const safeIncoming = incoming.filter((x) => !shouldHideNotification(x));

        state.items =
          mode === "replace"
            ? uniqByIdDesc(safeIncoming)
            : mergeItems(state.items, safeIncoming);

        state.lastFetchedAt = fetchedAt || new Date().toISOString();
        state.lastMode = mode || null;

        // FAZ-49: sayaç OTORİTESİ backend. Eskiden her fetch'te yerelden
        // yeniden hesaplanıyor ve `action.payload.unreadCount` hiç
        // kullanılmıyordu → rozet yalnız bellekteki (limit'li, birim-filtreli)
        // satırlardan hesaplandığı için gerçek sayıyla kalıcı sapıyordu.
        // Yerel hesap artık YALNIZ iyimser düşüş için (okundu tıklaması).
        const backendUnread = Number(action?.payload?.unreadCount);
        state.unreadCountAll = Number.isFinite(backendUnread)
          ? backendUnread
          : computeUnreadCount(state.items, state.readIds);

        // FAZ-77: kaynak-başı sayaçların OTORİTESİ de backend. Kaynak süzgeçli
        // (segment) isteklerde de tüm kaynakların sayısı döner — bu yüzden
        // sekme rozetleri hangi sekmede olursanız olun doğru kalır.
        const bs = action?.payload?.unreadBySource;
        if (bs && typeof bs === "object") {
          state.unreadBySource = { ...bs };
          // FAZ-85: sunucu konuştu. Bundan sonra 0 da GEÇERLİ bir cevaptır.
          state.unreadLoaded = true;
        }
        if (Number.isFinite(backendUnread)) state.unreadLoaded = true;

        // persist readIds (throttle)
        persistReadIdsToStorage(state.readIds);

        log("fetch:fulfilled", {
          silent,
          incoming: safeIncoming.length,
          stored: state.items.length,
          unreadCountAll: state.unreadCountAll,
          mode,
          skipped: Boolean(skipped),
          droppedCancelled: Number(droppedCancelled || 0),
          lastFetchedAt: state.lastFetchedAt,
          persistedReadIds: Object.keys(state.readIds || {}).length,
          userId: readCurrentUserIdSafe(),
        });
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        const silent = Boolean(action?.meta?.arg?.silent);
        if (!silent) state.loading = false;

        state.error =
          action.payload || action.error?.message || "Bildirimler alınamadı.";
        warn("fetch:rejected", { silent, error: state.error });
      });

    // markRead
    builder
      .addCase(markRead.pending, (state, action) => {
        state.markingRead = true;
        state.error = null;
        log("markRead:pending", action?.meta?.arg || {});
      })
      .addCase(markRead.fulfilled, (state, action) => {
        state.markingRead = false;

        const ids = safeIds(action.payload?.ids);
        let yeniOkunan = 0;
        for (const id of ids) {
          if (!state.readIds[id]) {
            yeniOkunan += 1;
            dusurKaynakSayaci(state, itemById(state, id));
          }
          state.readIds[id] = true;
        }
        // İyimser düşüş; backend değeri sonraki fetch'te geri yazar.
        state.unreadCountAll = Math.max(
          0, Number(state.unreadCountAll || 0) - yeniOkunan
        );

        persistReadIdsToStorage(state.readIds);

        log("markRead:fulfilled", {
          count: ids.length,
          unreadCountAll: state.unreadCountAll,
          persistedReadIds: Object.keys(state.readIds || {}).length,
          userId: readCurrentUserIdSafe(),
        });
      })
      .addCase(markRead.rejected, (state, action) => {
        state.markingRead = false;
        state.error =
          action.payload || action.error?.message || "Okundu işaretleme başarısız.";
        warn("markRead:rejected", { error: state.error });
      })
      // FAZ-49: tümünü okundu — bellekteki her satır okundu, sayaç sıfır.
      .addCase(markAllRead.pending, (state) => {
        state.markingRead = true;
      })
      .addCase(markAllRead.fulfilled, (state, action) => {
        state.markingRead = false;
        // FAZ-77: kapsam AÇIK SEKME ise yalnız o kaynakların satırları okunur.
        const kapsam = safeStr(action.payload?.source)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        const kapsamli = kapsam.length > 0;

        for (const it of Array.isArray(state.items) ? state.items : []) {
          const id = Number(it?.id);
          if (!id) continue;
          if (kapsamli && !kapsam.includes(kaynakOf(it))) continue;
          state.readIds[id] = true;
        }

        if (kapsamli) {
          for (const k of kapsam) state.unreadBySource[k] = 0;
          // Toplam sayaç KALAN kovalardan toplanır — "yüklü satırlardan say"
          // yaklaşımı 150'lik pencere yüzünden yanlış olurdu (backend'in
          // kaynak-başı sayacı ise tamdır).
          state.unreadCountAll = Object.values(state.unreadBySource).reduce(
            (a, n) => a + (Number(n) || 0),
            0
          );
        } else {
          state.unreadCountAll = 0;
          // FAZ-85 — KOVALAR SİLİNMEZ, SIFIRLANIR.
          //
          // Eskiden burada sözlük `{}` yapılıyordu. Yeni anlamda `{}` = "sunucu
          // hiç konuşmadı" olduğu için "tümünü okundu"dan hemen sonra sayaç
          // seçicileri yerel/bayat hesaba düşüyor ve rozet geri zıplayabiliyordu
          // — yani okuma işlemi sayacı sıfırlamıyordu.
          for (const k of Object.keys(state.unreadBySource)) {
            state.unreadBySource[k] = 0;
          }
        }

        persistReadIdsToStorage(state.readIds);
        log("markAllRead:fulfilled", {
          updated: action.payload?.updated,
          kapsam: kapsamli ? kapsam : "hepsi",
        });
      })
      .addCase(markAllRead.rejected, (state, action) => {
        state.markingRead = false;
        state.error =
          action.payload || action.error?.message || "Okundu işaretleme başarısız.";
        warn("markAllRead:rejected", { error: state.error });
      });

    // FAZ-33: tercihler
    builder
      .addCase(fetchPreferences.fulfilled, (state, action) => {
        state.preferences = { ...DEFAULT_PREFERENCES, ...(action.payload || {}) };
        state.preferencesLoaded = true;
      })
      .addCase(savePreferences.pending, (state) => {
        state.preferencesSaving = true;
      })
      .addCase(savePreferences.fulfilled, (state, action) => {
        state.preferencesSaving = false;
        state.preferences = { ...DEFAULT_PREFERENCES, ...(action.payload || {}) };
        state.preferencesLoaded = true;
      })
      .addCase(savePreferences.rejected, (state, action) => {
        state.preferencesSaving = false;
        state.error =
          action.payload || action.error?.message || "Bildirim ayarları kaydedilemedi.";
      });
  },
});

export const {
  clearNotifications,
  markOneAsReadLocal,
  hydrateReadIdsFromStorage,
} = belediyeNotificationsSlice.actions;

export default belediyeNotificationsSlice.reducer;

// -------------------------------------------------------------
// Selectors
// -------------------------------------------------------------
export const selectNotificationsState = (state) => state?.belediyeNotifications;

export const selectNotificationsLoading = (state) =>
  Boolean(selectNotificationsState(state)?.loading);

export const selectNotificationsError = (state) =>
  selectNotificationsState(state)?.error || null;

export const selectNotificationsMarkingRead = (state) =>
  Boolean(selectNotificationsState(state)?.markingRead);

export const selectNotificationsAll = (state) =>
  selectNotificationsState(state)?.items || [];

/**
 * Aktif birime göre filtre:
 * - unit_slug varsa eşit olmalı
 * - unit_slug yoksa göster (fail-safe)
 * - cancelled her yerde gizli
 */
export const selectNotificationsForActiveUnit = (state, activeUnitSlugArg) => {
  const activeUnitSlug = safeStr(activeUnitSlugArg || readActiveUnitSlugSafe()).trim();
  const items = selectNotificationsAll(state);

  const filtered = items.filter((it) => {
    if (shouldHideNotification(it)) return false;

    if (!activeUnitSlug) return true;
    const slug = extractUnitSlugFromItem(it);
    if (!slug) return true; // fail-safe
    return slug === activeUnitSlug;
  });

  if (DEBUG) {
    log("selector:selectNotificationsForActiveUnit", {
      activeUnitSlug,
      total: items.length,
      filtered: filtered.length,
    });
  }

  return filtered;
};

/**
 * 3) Dropdown için: unread-first (limit=15)
 */
export const selectDropdownNotificationsForActiveUnit = (
  state,
  activeUnitSlugArg,
  limit = 15
) => {
  const s = selectNotificationsState(state);
  const base = selectNotificationsForActiveUnit(state, activeUnitSlugArg);
  const ordered = prioritizeUnreadThenRead(base, s?.readIds || {});
  return ordered.slice(0, Math.max(1, Number(limit) || 15));
};

// FAZ-60 — ZİL MESAJ TAŞIMAZ.
//
// Kullanıcı (2026-07-28): "navbardaki bildirimler yapısındaki mesajlar yapısını
// kaldır; orada mesaj olmayacak hiçbir şekilde."
//
// Gerekçe: mesajın kendi yüzeyi var (sağ-alttaki sohbet penceresi ve onun
// başlatıcı balonu). FAZ-51'de kurulan mesaj→bildirim köprüsü zili sohbet
// trafiğiyle dolduruyor, kurumsal olaylar (kapatma talebi, görev, duyuru)
// arasında kayboluyordu.
//
// KAPSAM yalnız ZİL: "Tümü" sayfasındaki Mesajlar segmenti DURUYOR — bildirim
// kaydı silinmiyor, yalnız zilin kapsamı daralıyor. Bu yüzden sayfanın
// kullandığı selector'lara DOKUNULMADI; zil için ayrı ikisi var.
const MESAJ_KAYNAK = "mesajlasma";
export const zilTasirMi = (n) => {
  const kaynak = safeStr(n?.source_module || n?.inapp_payload?.source_module).trim();
  if (kaynak === MESAJ_KAYNAK) return false;
  // Kaynak alanı boş gelen eski satırlar için olay kodu yedek ölçüt.
  const ev = safeStr(n?.event || n?.inapp_payload?.event).trim();
  return !ev.startsWith("mesaj.");
};

/** Zil listesi: mesajlar HARİÇ, unread-first (limit=15). */
export const selectBellDropdownNotifications = (state, activeUnitSlugArg, limit = 15) => {
  const s = selectNotificationsState(state);
  const base = selectNotificationsForActiveUnit(state, activeUnitSlugArg).filter(zilTasirMi);
  const ordered = prioritizeUnreadThenRead(base, s?.readIds || {});
  return ordered.slice(0, Math.max(1, Number(limit) || 15));
};

/** Zil rozeti: mesajlar HARİÇ okunmamış sayısı (liste ile aynı kapsam —
 *  görülemeyecek bir şeyi saymak rozeti yalancı yapardı).
 *
 *  FAZ-77: sayı artık backend'in kaynak-başı sayacından türetilir. Eskiden
 *  bellekteki (limit'li) satırlardan sayılıyordu → 150'lik pencerenin dışında
 *  kalan okunmamışlar rozete hiç yansımıyordu ve rozet, sayfadaki "Okunmamış"
 *  çipiyle sürekli çelişiyordu. Kaynak sayaçları yoksa (ilk yükleme) eski
 *  yerel hesap yedek kalır. */
/** FAZ-85: sunucu sayıları bir kez döndü mü? (0 da geçerli bir cevaptır.)
 *  Eski/dış kaynaklı durumda bayrak yoksa dolu sözlük de "geldi" sayılır —
 *  yani davranış hiçbir koşulda eskisinden kötü olmaz. */
const sunucuSayilariGeldi = (s) =>
  Boolean(s?.unreadLoaded) || Object.keys(s?.unreadBySource || {}).length > 0;

export const selectBellUnreadCount = (state, activeUnitSlugArg) => {
  const s = selectNotificationsState(state);
  // FAZ-85: kapı "sözlük dolu mu" DEĞİL, "sunucu konuştu mu". Aradaki fark tam
  // sıfırda ortaya çıkıyordu: hepsi okununca sözlük boşalıyor, seçici bunu
  // "henüz yüklenmedi" sanıp bellekteki satırları sayan yedeğe düşüyordu.
  if (sunucuSayilariGeldi(s)) {
    return Object.entries(s.unreadBySource || {}).reduce(
      (acc, [k, n]) => (k === MESAJ_KAYNAK ? acc : acc + (Number(n) || 0)),
      0
    );
  }
  const items = selectNotificationsForActiveUnit(state, activeUnitSlugArg).filter(zilTasirMi);
  return computeUnreadCount(items, s?.readIds || {});
};

/**
 * FAZ-85 — İKONLARIN TEK SORUSU: okunmamış VAR MI?
 *
 * Kullanıcı (2026-08-01): "sidebardaki bildirim ikonunun üzerinde ve
 * navbardakinin üzerinde minik bir kırmızı nokta olsun, eğer bir tane bile
 * okunmamış bildirim varsa — sayaç değil."
 *
 * Bu yüzden nokta TEK bir boole'den beslenir ve kapsamı TÜM kaynaklardır
 * (mesajlar dahil). Zil rozeti mesajı hariç tutuyordu, sidebar hiç sayı
 * göstermiyordu; aynı ekranda iki farklı "okunmamış" tanımı vardı. Nokta ikisini
 * de aynı cevaba bağlar: "bakılacak bir şey var mı?"
 */
export const selectHasUnread = (state) => {
  const s = selectNotificationsState(state);
  if (sunucuSayilariGeldi(s)) {
    return Object.values(s.unreadBySource || {}).some((n) => (Number(n) || 0) > 0);
  }
  // Sunucu henüz konuşmadıysa yereli kullan — nokta gecikmeli değil, ihtiyatlı.
  return Number(s?.unreadCountAll || 0) > 0;
};

/** Zil penceresinin altındaki "okunmamış mesaj" çizgisi (FAZ-77).
 *
 *  Zil mesaj TAŞIMAZ (FAZ-60 kararı korunuyor) ama mesajın okunmamışını hiç
 *  söylememek de sinyali kaybediyordu. Ayrı bir satır olarak duruyor: zil
 *  kirlenmiyor, sinyal kaybolmuyor, iki sayı da çelişmiyor. */
export const selectMesajUnreadCount = (state) => {
  const s = selectNotificationsState(state);
  return Number(s?.unreadBySource?.[MESAJ_KAYNAK] || 0);
};

/** Kaynak başına okunmamış (sekme rozetleri) — otorite backend. */
export const selectUnreadBySource = (state) =>
  selectNotificationsState(state)?.unreadBySource || {};

/**
 * Badge count: aktif birime göre unread.
 *
 * FAZ-77: otorite BACKEND (`unreadCountAll`; feed isteği X-Unit-Slug ile
 * gittiği için sunucudaki sayı da aktif-birim kapsamlıdır). Yerel hesap yalnız
 * ilk yükleme öncesi yedek. Eskiden burada yüklü satırlar sayılıyor, zil ise
 * kendi kapsamını sayıyordu → aynı ekranda iki farklı "okunmamış" sayısı.
 */
export const selectUnreadCountForActiveUnit = (state, activeUnitSlugArg) => {
  const s = selectNotificationsState(state);
  if (sunucuSayilariGeldi(s)) {
    return Object.values(s.unreadBySource || {}).reduce((a, n) => a + (Number(n) || 0), 0);
  }
  const items = selectNotificationsForActiveUnit(state, activeUnitSlugArg);
  const unread = computeUnreadCount(items, s?.readIds || {});
  if (DEBUG) {
    log("selector:selectUnreadCountForActiveUnit", {
      activeUnitSlug: safeStr(activeUnitSlugArg || readActiveUnitSlugSafe()).trim(),
      unread,
      items: items.length,
      persistedReadIds: Object.keys(s?.readIds || {}).length,
      userId: readCurrentUserIdSafe(),
    });
  }
  return unread;
};

export const selectUnreadCountAll = (state) =>
  Number(selectNotificationsState(state)?.unreadCountAll || 0);

export const selectNotificationIsRead = (state, id) => {
  const s = selectNotificationsState(state);
  const nid = Number(id);
  if (!s || !nid) return false;

  if (s.readIds?.[nid]) return true;

  const it = (s.items || []).find((x) => Number(x?.id) === nid);
  return Boolean(it?.is_read);
};

// FAZ-33 — tercih selector'ları
export const selectPreferences = (state) =>
  selectNotificationsState(state)?.preferences || {};

export const selectPreferencesLoaded = (state) =>
  Boolean(selectNotificationsState(state)?.preferencesLoaded);

export const selectPreferencesSaving = (state) =>
  Boolean(selectNotificationsState(state)?.preferencesSaving);

export const selectSoundEnabled = (state) =>
  Boolean(selectNotificationsState(state)?.preferences?.sound_enabled);

export const selectToastEnabled = (state) =>
  Boolean(selectNotificationsState(state)?.preferences?.toast_enabled);

export const selectMutedCategories = (state) =>
  selectNotificationsState(state)?.preferences?.muted_categories || [];